import { RunningExperiments, useExperiments } from 'state/experiments';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { isMobile } from 'utils/environment';

/**
 * Depending on the experiment variant, the user will see the following:
 *
 * | Variant    | Mobile (mobile web)    | Web                 |
 * |------------|------------------------|---------------------|
 * | Control    | Home Page Feed         | Home Page Feed      |
 * | Treatment  | News Feed              | Home Page Feed      |
 *
 */
export enum ContentCardNotificationsExperimentVariants {
  CONTROL = 'control',
  TREATMENT = 'treatment',
}

type ContentCardsNotificationsExperimentResult = {
  isNewsFeedEnabled: boolean;
  isHomePageFeedEnabled: boolean;
  isExperimentEnabled: boolean;
  experimentToExpose?: RunningExperiments;
};

type ResolveContentCardsNotificationsExperimentArgs = {
  showBrazeContentCards: boolean;
  isLoadingExperiment: boolean;
  isControl: boolean;
  isTreatment: boolean;
  isMobileDevice: boolean;
  contentCardsExperiment: RunningExperiments;
};

export const resolveContentCardsNotificationsExperiment = ({
  showBrazeContentCards,
  isLoadingExperiment,
  isControl,
  isTreatment,
  isMobileDevice,
  contentCardsExperiment,
}: ResolveContentCardsNotificationsExperimentArgs): ContentCardsNotificationsExperimentResult => {
  // Flag OFF. Always show Sanity Cards on Homepage. Don't show News section.
  if (!showBrazeContentCards) {
    return {
      isNewsFeedEnabled: false,
      isHomePageFeedEnabled: false,
      isExperimentEnabled: false,
    };
  }

  if (isLoadingExperiment) {
    return {
      isNewsFeedEnabled: false,
      isHomePageFeedEnabled: false,
      isExperimentEnabled: false,
    };
  }

  const hasResolvedExperimentVariant = isControl || isTreatment;
  const isNewsFeedEnabled = isMobileDevice && isTreatment;
  const isHomePageFeedEnabled =
    isControl || (isTreatment && !isMobileDevice) || !hasResolvedExperimentVariant;
  const isExperimentEnabled = isNewsFeedEnabled || isHomePageFeedEnabled;

  return {
    isNewsFeedEnabled,
    isHomePageFeedEnabled,
    isExperimentEnabled,
    ...(hasResolvedExperimentVariant && { experimentToExpose: contentCardsExperiment }),
  };
};

export const useContentCardsNotificationsExperiment = () => {
  const showBrazeContentCards = useFlag(LaunchDarklyFlag.SHOW_BRAZE_CONTENT_CARDS_ON_HOMEPAGE);

  const { isLoading: isLoadingExperiment, variant } = useExperiments();

  const contentCardsExperiment = RunningExperiments.NotificationCenterTest;

  const isTreatment = variant(contentCardsExperiment, 'treatment', {
    skipExposureLogging: true,
  });

  const isControl = variant(contentCardsExperiment, 'control', {
    skipExposureLogging: true,
  });

  return resolveContentCardsNotificationsExperiment({
    showBrazeContentCards,
    isLoadingExperiment,
    isControl,
    isTreatment,
    isMobileDevice: isMobile(),
    contentCardsExperiment,
  });
};
